import { color, spacing, Text } from '@lmn/design-system';
import React from 'react';
import type { FC } from 'react';
import styled, { css } from 'styled-components';
import { IconChangeAirport } from '@/app/common/icons/libraryIcons';
import type { TransportStop } from '@lmn/lmn-components/components/Trip/shared/transport/transportStopsAdapter.d.ts';
import { AirlineLogo } from '@/app/common/components/AirlineLogo';
import { Icon } from '@/app/common/icons/Icon';
import type { StarfishIconsByCategory } from '@/app/common/icons/starfishManifest';

const StopsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
`;

export type AirlineCompany = { code: string; name: string; type?: string };

// The design system has no "multiple trains" glyph, so a mixed-carrier train leg shows the plain train.
const TRANSPORT_TYPE_ICONS: Record<string, StarfishIconsByCategory['Transportation']> = {
  FLIGHT: 'icon-airplane-up',
  TRAIN: 'icon-train',
  BUS: 'icon-bus',
};

const MixedTransportIcon: FC<{ companies: AirlineCompany[] }> = ({ companies }) => {
  const uniqueTypes = [...new Set(companies.map((c) => c.type).filter(Boolean) as string[])]
    .filter((type) => TRANSPORT_TYPE_ICONS[type])
    .slice(0, 2);
  if (uniqueTypes.length < 2) return null;
  return (
    <MixedTransportPill>
      {uniqueTypes.map((type) => (
        <Icon
          key={type}
          category="Transportation"
          name={TRANSPORT_TYPE_ICONS[type]}
          size={16}
          color="iconPrimary"
          alt=""
          css="flex-shrink: 0;"
        />
      ))}
    </MixedTransportPill>
  );
};

export type EvoTransportTimelineProps = {
  duration: string;
  stops: TransportStop[];
  stopLabel: string;
  showIcons?: boolean;
  providerName: string;
  providerCode: string;
  companies?: AirlineCompany[];
};

export const EvoTransportTimeline: React.FC<EvoTransportTimelineProps> = ({
  duration,
  stops,
  stopLabel,
  showIcons,
  providerName,
  providerCode,
  companies,
}) => {
  const showMultipleLogos = companies && companies.length > 1;
  const hasMixedTypes = showMultipleLogos && new Set(companies.map((c) => c.type).filter(Boolean)).size > 1;
  return (
    <TravelSummary>
      <LineWrapper>
        <LinePart />
        <DurationOnLine>
          {/* MPH-398: Figma duration text is 16px (Body), not 14px (BodySmall) */}
          <Text variant="Body" color="textSecondary">
            {duration}
          </Text>
        </DurationOnLine>
        <LinePart />
        <Dots>
          {stops.map(
            (value) => value && <Dot key={value.id} hasLocationChange={isStopChangingLocationEligible(value)} />
          )}
        </Dots>
      </LineWrapper>
      <StopsContainer>
        {hasMixedTypes ? (
          <MixedTransportIcon companies={companies} />
        ) : showMultipleLogos ? (
          <MultiAirlineLogos companies={companies} />
        ) : (
          <SingleAirlineLogoContainer>
            <AirlineLogo code={providerCode} name={providerName} iconSize={20} transportType={companies?.[0]?.type} />
          </SingleAirlineLogoContainer>
        )}
        <Stops stopLabel={stopLabel} stops={stops} showIcons={showIcons} />
      </StopsContainer>
    </TravelSummary>
  );
};

const MultiAirlineLogos: FC<{ companies: AirlineCompany[] }> = ({ companies }) => (
  <MultiLogoContainer>
    {companies.slice(0, 2).map((company, i) => (
      <LogoBadge key={company.code} $isSecond={i > 0}>
        <AirlineLogo code={company.code} name={company.name} iconSize={20} transportType={company.type} />
      </LogoBadge>
    ))}
  </MultiLogoContainer>
);

type StopsProps = {
  stopLabel: string;
  stops: TransportStop[];
  showIcons?: boolean;
};

const Stops: React.FC<StopsProps> = ({ stopLabel, stops, showIcons }) => (
  <>
    {/* MPH-398: Figma stop label ("Direct") is 16px (Body), not 14px (BodySmall) */}
    <Text variant="Body" color="textSecondary">
      {stopLabel}
    </Text>
    {showIcons && stops.find((stop) => isStopChangingLocationEligible(stop)) && <StyledIconChangeAirport />}
  </>
);

const isStopChangingLocationEligible = (stop: TransportStop): boolean => !stop.isTechnical && stop.isChangingLocation;

const TravelSummary = styled.div`
  text-align: center;
`;

const LineWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin: 6px 0;
`;

const LinePart = styled.div`
  flex: 1;
  height: 1px;
  background-color: ${color('gray300')};
`;

const DurationOnLine = styled.div`
  display: flex;
  align-items: center;
  padding: 0 6px;
  white-space: nowrap;
`;

const Dots = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-evenly;
  padding: 0 16%;
`;

const Dot = styled.div<{ hasLocationChange: boolean }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background-color: ${({ hasLocationChange }) => (hasLocationChange ? color('warning600') : color('gray200'))};
  border: 2px solid ${color('gray0')};
`;

const StyledIconChangeAirport = styled(IconChangeAirport)`
  position: relative;
  top: 3px;
  margin-left: ${spacing(0.5)};
  color: ${color('warning600')};
`;

const SingleAirlineLogoContainer = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
`;

const MultiLogoContainer = styled.div`
  display: flex;
  align-items: center;
`;

const MixedTransportPill = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 24px;
  padding: 2px 8px;
  gap: 4px;
  border-radius: 12px;
  border: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderNeutral};
  background-color: ${(p) => p.theme.kallisto.color.background.static.backgroundLayer02};
  flex-shrink: 0;
  box-sizing: border-box;
`;

const LogoBadge = styled.div<{ $isSecond: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderNeutral};
  background-color: ${(p) => p.theme.kallisto.color.background.static.backgroundLayer02};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${(p) =>
    p.$isSecond &&
    css`
      margin-left: -8px;
      box-shadow: 0 0 0 2px ${p.theme.kallisto.color.background.static.backgroundLayer02};
    `}
`;
